<template>
  <div>
    <div class="page-header">
      <h2><span class="breadcrumb-level">订单管理</span><span class="breadcrumb-sep">/</span><span class="breadcrumb-level">售后</span><span class="breadcrumb-sep">/</span><span class="breadcrumb-level breadcrumb-current">退款管理</span></h2>
      <div class="header-actions">
        <el-select v-model="filterStatus" placeholder="筛选状态" clearable style="width:140px" @change="fetchData">
          <el-option label="待退款" value="pending" />
          <el-option label="已退款" value="refunded" />
          <el-option label="退款失败" value="failed" />
        </el-select>
        <el-input v-model="searchKeyword" placeholder="搜索订单号" clearable prefix-icon="Search" style="width:200px" @input="fetchData" />
      </div>
    </div>

    <el-table :data="list" stripe v-loading="loading">
      <el-table-column label="订单号" min-width="150">
        <template #default="{row}">{{row.order?.order_no||'-'}}</template>
      </el-table-column>
      <el-table-column label="订单类型" width="80">
        <template #default="{row}">{{typeLabel(row.order?.order_type)}}</template>
      </el-table-column>
      <el-table-column label="退款金额" width="100">
        <template #default="{row}">¥{{row.refund_amount}}</template>
      </el-table-column>
      <el-table-column label="支付渠道" width="80">
        <template #default="{row}">{{channelLabel(row.pay_channel)}}</template>
      </el-table-column>
      <el-table-column label="退款状态" width="90">
        <template #default="{row}">
          <el-tag v-if="row.status==='refunded'" type="success" size="small">已退款</el-tag>
          <el-tag v-else-if="row.status==='failed'" type="danger" size="small">退款失败</el-tag>
          <el-tag v-else type="warning" size="small">待退款</el-tag>
        </template>
      </el-table-column>
      <el-table-column label="失败原因" min-width="150" show-overflow-tooltip>
        <template #default="{row}">{{row.failed_reason||'-'}}</template>
      </el-table-column>
      <el-table-column label="重试次数" width="70">
        <template #default="{row}">{{row.retry_count||0}}次</template>
      </el-table-column>
      <el-table-column label="处理人" width="90">
        <template #default="{row}">{{row.processor?.name||'-'}}</template>
      </el-table-column>
      <el-table-column label="退款时间" width="140">
        <template #default="{row}">{{formatDateTime(row.processed_at)}}</template>
      </el-table-column>
      <el-table-column label="操作" width="120" fixed="right">
        <template #default="{row}">
          <el-button v-if="row.status==='pending'" size="small" type="primary" @click="handleRefund(row)" :loading="refundingId===row.id">发起退款</el-button>
          <el-button v-else-if="row.status==='failed'" size="small" type="danger" @click="handleRefund(row)" :loading="refundingId===row.id">重试退款</el-button>
          <span v-else style="color:#909399">-</span>
        </template>
      </el-table-column>
    </el-table>

    <div class="pagination-wrap" v-if="pagination.total>0">
      <el-pagination
        v-model:current-page="pagination.current_page"
        :page-size="pagination.per_page"
        :total="pagination.total"
        layout="total, prev, pager, next"
        @current-change="onPageChange"
      />
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { ElMessage, ElMessageBox } from 'element-plus';
import * as api from '../../api/orders';

const list = ref([]);
const loading = ref(false);
const filterStatus = ref('');
const searchKeyword = ref('');
const refundingId = ref(null);
const pagination = ref({ current_page: 1, per_page: 20, total: 0 });

const typeLabel = (t) => ({ mall:'商城', hotel:'酒店', park:'乐园', daycare:'日托', activity:'活动', recharge:'充值' }[t]||t||'-');
const channelLabel = (c) => ({ balance:'余额', wechat:'微信', mixed:'混合' }[c]||c||'-');
const formatDateTime = (v) => { if (!v) return '-'; const d = new Date(v); if (isNaN(d.getTime())) return v; return d.toLocaleString('zh-CN', { year:'numeric', month:'2-digit', day:'2-digit', hour:'2-digit', minute:'2-digit' }); };

const fetchData = async () => {
  loading.value = true;
  try {
    const params = { page: pagination.value.current_page, per_page: pagination.value.per_page };
    if (filterStatus.value) params.status = filterStatus.value;
    if (searchKeyword.value) params.search = searchKeyword.value;
    const r = await api.getRefundList(params);
    const d = r.data || {};
    list.value = d.data || [];
    pagination.value = d.pagination || d || { current_page: 1, per_page: 20, total: 0 };
  } catch {} finally { loading.value = false; }
};

const onPageChange = (page) => { pagination.value.current_page = page; fetchData(); };

const handleRefund = async (row) => {
  const actionLabel = row.status === 'failed' ? '重试退款' : '发起退款';
  try {
    await ElMessageBox.confirm(`确认${actionLabel} ¥${row.refund_amount}？`, actionLabel, { confirmButtonText:'确认', cancelButtonText:'取消', type:'warning' });
    refundingId.value = row.id;
    await api.processRefund(row.id);
    ElMessage.success('退款成功');
    await fetchData();
  } catch (e) {
    if (e !== 'cancel') ElMessage.error(e.response?.data?.message || '退款失败');
  } finally { refundingId.value = null; }
};

onMounted(fetchData);
</script>

<style scoped>
.header-actions { display: flex; gap: 12px; align-items: center; }
.pagination-wrap { display: flex; justify-content: flex-end; margin-top: 16px; }
</style>
